/**
 * combatHud.ts — everything the player reads during a fight: the swing chevron,
 * health bars, floating damage numbers and the kill feed.
 *
 * Contact feedback is not optional (AGENTS.md: contacts are sounded from the server): tuning has to be
 * visible, so every hit prints what it did, where it landed on the blade and how
 * fast it was going. These are DOM overlays projected from world space rather
 * than in-scene sprites — crisp text at any distance, and no meshes to dispose.
 */

import * as THREE from 'three';
import { FEEL, type CombatEvent, type PlayerInfo } from '@dueler/shared';

/** How long a damage number lives, ms. */
const NUMBER_LIFE_MS = 1100;
/** How long a kill-feed line lives, ms. */
const FEED_LIFE_MS = 6000;
const FEED_MAX = 5;

interface FloatingNumber {
  el: HTMLDivElement;
  world: THREE.Vector3;
  bornAt: number;
}

export interface CombatHud {
  /** Feeds one server combat event in. */
  onEvent(event: CombatEvent, now: number, selfSlot: number, nameOf: (slot: number) => string): void;
  /** Re-projects everything. Call once per frame after the camera has moved. */
  update(
    now: number,
    camera: THREE.PerspectiveCamera,
    positions: Map<number, THREE.Vector3>,
    healths: Map<number, number>,
    players: Map<number, PlayerInfo>,
    selfSlot: number,
    dial: number,
  ): void;
  dispose(): void;
}

export function createCombatHud(): CombatHud {
  const root = document.createElement('div');
  root.className = 'combat-hud';
  root.innerHTML = `
    <div class="swing-chevron" id="chevron"></div>
    <div class="killfeed" id="killfeed"></div>
    <div class="selfbar" id="selfbar"><div class="selfbar-fill" id="selfbar-fill"></div><span id="selfbar-text"></span></div>
    <div class="world-layer" id="world"></div>
  `;
  document.body.appendChild(root);

  const chevron = root.querySelector<HTMLDivElement>('#chevron')!;
  const killfeed = root.querySelector<HTMLDivElement>('#killfeed')!;
  const selfFill = root.querySelector<HTMLDivElement>('#selfbar-fill')!;
  const selfText = root.querySelector<HTMLSpanElement>('#selfbar-text')!;
  const worldLayer = root.querySelector<HTMLDivElement>('#world')!;

  const numbers: FloatingNumber[] = [];
  /** slot → the name+health plate floating over that player. */
  const plates = new Map<number, HTMLDivElement>();
  const feedLines: { el: HTMLDivElement; bornAt: number }[] = [];

  const project = new THREE.Vector3();

  function addNumber(text: string, cls: string, world: THREE.Vector3, now: number): void {
    const el = document.createElement('div');
    el.className = `dmg ${cls}`;
    el.textContent = text;
    worldLayer.appendChild(el);
    numbers.push({ el, world: world.clone(), bornAt: now });
  }

  function addFeed(text: string, now: number): void {
    const el = document.createElement('div');
    el.className = 'feed-line';
    el.textContent = text;
    killfeed.appendChild(el);
    feedLines.push({ el, bornAt: now });
    while (feedLines.length > FEED_MAX) {
      feedLines.shift()?.el.remove();
    }
  }

  function plateFor(slot: number): HTMLDivElement {
    const existing = plates.get(slot);
    if (existing) return existing;
    const el = document.createElement('div');
    el.className = 'plate';
    el.innerHTML = `<div class="plate-name"></div><div class="plate-bar"><div class="plate-fill"></div></div>`;
    worldLayer.appendChild(el);
    plates.set(slot, el);
    return el;
  }

  return {
    onEvent(event, now, selfSlot, nameOf) {
      switch (event.t) {
        case 'hit': {
          const world = new THREE.Vector3(event.x, event.y, event.z);
          const mine = event.attacker === selfSlot;
          // The blade fraction and speed are what you tune against: a 0.9 at
          // 18 m/s hitting for 54 tells you the tip is doing its job.
          const detail = mine ? ` ${event.zone} · ${event.speed}m/s · blade ${event.blade}` : '';
          addNumber(`${event.damage}${detail}`, mine ? 'mine' : 'theirs', world, now);
          break;
        }
        case 'block':
          addNumber(
            event.target === selfSlot ? 'RIPOSTE' : 'block',
            event.target === selfSlot ? 'parry' : 'block',
            new THREE.Vector3(event.x, event.y, event.z),
            now,
          );
          break;
        case 'parry':
          addNumber(
            event.target === selfSlot ? 'PARRY · RIPOSTE' : 'PARRY',
            'parry',
            new THREE.Vector3(event.x, event.y, event.z),
            now,
          );
          break;
        case 'chamber':
          addNumber('CHAMBERED', 'chamber', new THREE.Vector3(event.x, event.y, event.z), now);
          break;
        case 'kill':
          addFeed(
            event.attacker >= 0
              ? `${nameOf(event.attacker)} killed ${nameOf(event.target)}`
              : `${nameOf(event.target)} died`,
            now,
          );
          break;
        case 'respawn':
          addFeed(`${nameOf(event.slot)} respawned`, now);
          break;
      }
    },

    update(now, camera, positions, healths, players, selfSlot, dial) {
      // The chevron is drawn in screen space: 0 points right and grows
      // counter-clockwise, while CSS rotation grows clockwise, hence the sign.
      chevron.style.transform = `rotate(${-dial}rad)`;

      const selfHealth = healths.get(selfSlot);
      if (selfHealth !== undefined) {
        const pct = Math.max(0, Math.min(1, selfHealth / FEEL.MAX_HEALTH));
        selfFill.style.width = `${pct * 100}%`;
        selfFill.style.background = pct > 0.5 ? '#5ac46a' : pct > 0.25 ? '#d8b53f' : '#d8503f';
        selfText.textContent = `${Math.ceil(selfHealth)}`;
      }

      // Floating numbers rise and fade, tracking the world point they came from.
      for (let i = numbers.length - 1; i >= 0; i -= 1) {
        const n = numbers[i]!;
        const age = (now - n.bornAt) / NUMBER_LIFE_MS;
        if (age >= 1) {
          n.el.remove();
          numbers.splice(i, 1);
          continue;
        }
        project.copy(n.world);
        project.y += age * 0.8;
        project.project(camera);
        const behind = project.z > 1;
        n.el.style.display = behind ? 'none' : 'block';
        if (behind) continue;
        n.el.style.left = `${((project.x + 1) / 2) * window.innerWidth}px`;
        n.el.style.top = `${((1 - project.y) / 2) * window.innerHeight}px`;
        n.el.style.opacity = `${1 - age * age}`;
      }

      // Name + health plate over every other player.
      const seen = new Set<number>();
      for (const [slot, pos] of positions) {
        if (slot === selfSlot) continue;
        seen.add(slot);
        const el = plateFor(slot);
        project.copy(pos);
        project.y += FEEL.BODY_HEIGHT / 2 + 0.35;
        project.project(camera);
        if (project.z > 1) {
          el.style.display = 'none';
          continue;
        }
        el.style.display = 'block';
        el.style.left = `${((project.x + 1) / 2) * window.innerWidth}px`;
        el.style.top = `${((1 - project.y) / 2) * window.innerHeight}px`;

        const name = el.querySelector<HTMLDivElement>('.plate-name')!;
        name.textContent = players.get(slot)?.name ?? `slot ${slot}`;
        const health = healths.get(slot) ?? FEEL.MAX_HEALTH;
        const fill = el.querySelector<HTMLDivElement>('.plate-fill')!;
        const pct = Math.max(0, Math.min(1, health / FEEL.MAX_HEALTH));
        fill.style.width = `${pct * 100}%`;
        fill.style.background = pct > 0.5 ? '#5ac46a' : pct > 0.25 ? '#d8b53f' : '#d8503f';
      }
      // Anyone who left loses their plate — no orphan DOM.
      for (const [slot, el] of plates) {
        if (!seen.has(slot)) {
          el.remove();
          plates.delete(slot);
        }
      }

      for (let i = feedLines.length - 1; i >= 0; i -= 1) {
        const line = feedLines[i]!;
        if (now - line.bornAt > FEED_LIFE_MS) {
          line.el.remove();
          feedLines.splice(i, 1);
        }
      }
    },

    dispose() {
      root.remove();
      numbers.length = 0;
      plates.clear();
      feedLines.length = 0;
    },
  };
}
